// @flow

// Module dependencies
import i18n from 'app/common/lib/i18n';

/* eslint no-data-options-dot-notation:0*/
/* eslint no-restricted-properties:0 */

type NumberFormatOptions = {
  localeMatcher?: 'lookup' | 'best fit',
  style?: 'decimal' | 'currency' | 'percent',
  currency?: string,
  currencyDisplay?: 'symbol' | 'code' | 'name',
  useGrouping?: boolean,
  minimumIntegerDigits?: number,
  minimumFractionDigits?: number,
  maximumFractionDigits?: number,
  minimumSignificantDigits?: number,
  maximumSignificantDigits?: number,
};

const _getPluralFunction = function(localeCode) {
  return i18n.getPluralFunction(localeCode);
};

/**
 * Create the options object for toLocalString.
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toLocaleString
 *
 * @param {number=} maximumFractionDigits The maximum number of fraction digits to display.  If
 * undefined, then there is no maximum.
 * @param {number=} minimumFractionDigits The minimum number of fraction digits to display.  If
 * undefined, then there is no minimum.
 * @param {string=} style The format style to use (i.e., 'percent', 'currency').  If undefined, then
 * just use the plain number formatting style.
 * @param {string=} currency Currency code to use (i.e. 'USD', 'GBP').
 * @param {string=} currencyDisplay Style to display the formatted currency in
 * @return {!Object} options options to pass to number.toLocaleString
 * @private
 */
const _getNumberFormatOptions = function(
  maximumFractionDigits,
  minimumFractionDigits,
  style?: 'decimal' | 'currency' | 'percent',
  currency,
  currencyDisplay = 'symbol',
): NumberFormatOptions {
  const options: NumberFormatOptions = {
    maximumFractionDigits,
    minimumFractionDigits,
  };
  if (style) {
    options.style = style;
    if (style === 'currency') {
      options.currency = currency;
      options.currencyDisplay = currencyDisplay;
    }
  }
  return options;
};

/**
 * Gaussian/Banker's Rounding
 * http://stackoverflow.com/questions/3108986/gaussian-bankers-rounding-in-javascript
 *
 * @param {number} number The number we want to round using Banker's rounding.
 * @param {number=} decimalPlaces Specify the number of decimals to keep. If undefined, it's set to 0.
 * @return {number} Number after the rounding
 * @example
 * // Returns 2
 * evenRound(1.5)
 * @example
 * // Returns 1.54
 * evenRound(1.535, 2)
 * @example
 * // Returns 1.52
 * evenRound(1.525, 2)
 * @private
 */
const _evenRound = function(number: number, decimalPlaces?: number): number {
  const d: number = decimalPlaces || 0;
  const m = Math.pow(10, d);
  // Avoid rounding errors
  const n = +(d ? number * m : number).toFixed(8);
  const i = Math.floor(n),
    f = n - i;
  // Allow for rounding errors in f
  const e = 1e-8;
  // eslint-disable-next-line no-nested-ternary
  const r = f > 0.5 - e && f < 0.5 + e ? (i % 2 === 0 ? i : i + 1) : Math.round(n);
  return d ? r / m : r;
};

/**
 * Symbols by currency code
 */
export const currencySymbols = {
  USD: '$',
  GBP: '£',
  CAD: '$',
  EUR: '€',
  AUD: 'A$',
  NZD: 'NZ$',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
};

/**
 * Return a localized decimal formatted string.
 *
 * @param {string} localeCode
 * @param {number} number The number to format.
 * @param {number=} maximumFractionDigits The maximum number of fraction digits to display.  If
 * undefined, then there is no maximum.
 * @param {number=} minimumFractionDigits The minimum number of fraction digits to display.  If
 * undefined, then there is no minimum.
 * @param {string=} style The format style to use (i.e., 'percent', 'currency').  If undefined, then
 * just use the plain number formatting style.
 * @param {string=} currency Currency code to use (i.e. 'USD', 'GBP').
 * @param {string} currencyDisplay 'symbol' for '$', 'name' for 'USD'
 * @return {string} The formatted number for display.
 */
function numberFormat(
  localeCode: string,
  number: number,
  maximumFractionDigits?: number,
  minimumFractionDigits?: number,
  style?: 'decimal' | 'currency' | 'percent',
  currency?: string,
  currencyDisplay?: 'symbol' | 'code' | 'name',
) {
  const options = _getNumberFormatOptions(
    maximumFractionDigits,
    minimumFractionDigits,
    style,
    currency,
    currencyDisplay,
  );
  return number.toLocaleString([localeCode], options);
}

/**
 * This code matches numberfmt at web/jinja2ext/helpers.py
 */
export function prettyNumberFormat(
  localeCode: string,
  number: number,
  options?: {
    style?: 'decimal' | 'currency' | 'percent',
    shortform?: boolean,
    shortform_maximum_fraction_digits?: number,
    maximum_fraction_digits?: number,
    minimum_fraction_digits?: number,
    uplimit?: number,
    currency?: string,
  },
): string {
  let maximumFractionDigits, minimumFractionDigits;
  const style = (options && options.style) || undefined;
  const shortform: boolean = (options && options.shortform) || false;
  const uplimit: ?number = (options && options.uplimit) || undefined;
  const currency: ?string = (options && options.currency) || undefined;

  if (!!uplimit && number > uplimit) {
    return `${uplimit}+`;
  }

  if (style === 'currency') {
    maximumFractionDigits =
      options === undefined || options.maximum_fraction_digits === undefined
        ? 2
        : options.maximum_fraction_digits;
    minimumFractionDigits =
      options === undefined || options.minimum_fraction_digits === undefined
        ? 2
        : options.minimum_fraction_digits;
  } else {
    maximumFractionDigits =
      options === undefined || options.maximum_fraction_digits === undefined
        ? 0
        : options.maximum_fraction_digits;
    minimumFractionDigits =
      options === undefined || options.minimum_fraction_digits === undefined
        ? 0
        : options.minimum_fraction_digits;
  }

  // When shortening the number the format is overridden. It is different from format for numbers
  // without shortening. This way you could have minimum_fraction_digits = 2 for all non-shortened
  // numbers (e.g. $1.00) and have shortened versions with no required fraction digits (e.g. $1k)
  //
  // Examples (if shortform_maximum_fraction_digits = 2):
  // ... 1,000,000 becomes 1M (not 1.00M)
  // ... 1,540,000 becomes 1.54M (not 2M, if we had a maximum_fraction_digits of 0)
  if (shortform && number > 999) {
    maximumFractionDigits =
      options === undefined || options.shortform_maximum_fraction_digits === undefined
        ? 2
        : options.shortform_maximum_fraction_digits;
    minimumFractionDigits = 0;
  }

  if (maximumFractionDigits < minimumFractionDigits) {
    return String(number);
  }

  // Number to use for printing. Could be a shortened version of the original number.
  let numberToPrint = number;
  // Suffix to use when number is shortened.
  let numberSuffix = '';
  // The short-form tries to collapse large numbers down to more readable short-forms.
  // Uses formats from https://en.wikipedia.org/wiki/Metric_prefix
  // Examples:
  // ... 1,000,000 becomes 1M.

  // shorthand abbreviations for Chinese/Japanese/Korean
  const shorthandAbbreviationsCJK = [
    {
      '100M': {
        value: 100000000,
        localeKey: i18n._(
          '億',
          'Abbreviation for one hundred millions. This abbreviation is only used in Chinese, Japanese and Korean.',
        ),
      },
    },
    {
      '10k': {
        value: 10000,
        localeKey: i18n._(
          '万',
          'Abbreviation for ten thousand. This abbreviation is only used in Chinese, Japanese and Korean.',
        ),
      },
    },
  ];
  // shorthand abbreviations for non-Chinese/Japanese/Korean languages
  const shorthandAbbreviations = [
    {
      b: {
        value: 1000000000,
        localeKey: i18n._('b', 'Abbreviation for one billion.'),
      },
    },
    {
      m: {
        value: 1000000,
        localeKey: i18n._('m', 'Abbreviation for one million.'),
      },
    },
    {
      k: {
        value: 1000,
        localeKey: i18n._('k', 'Abbreviation for one thousand.'),
      },
    },
  ];
  const useMan = /^zh|ja|ko/.test(localeCode);
  if (shortform && number > 999) {
    const abbr: Array<Object> = useMan ? shorthandAbbreviationsCJK : shorthandAbbreviations;
    for (let i = 0; i < abbr.length; i += 1) {
      const key = Object.keys(abbr[i])[0];
      const shorthandLimit = abbr[i][key].value;
      if (number >= shorthandLimit) {
        // rounds to the proper number of decimal places.
        numberToPrint =
          _evenRound((number / shorthandLimit) * Math.pow(10, maximumFractionDigits)) /
          Math.pow(10, maximumFractionDigits);
        numberSuffix = abbr[i][key].localeKey;
        break;
      }
    }
  }

  const formattedNumber = numberFormat(
    localeCode,
    numberToPrint,
    maximumFractionDigits,
    minimumFractionDigits,
    style,
    currency || undefined,
  );

  return formattedNumber + numberSuffix;
}

/**
 * @param {string} localeCode
 * @param {string} singular The singular version of the string to translate.
 * @param {string} plural The plural version of the string to translate.
 * @param {number} count The number that will determine whether to show singular or plural form
 */
function ngettext(localeCode: string, singular: string, plural: string, count: number): string {
  // unary + operator is intentional -- some of the plural expressions return booleans
  // we need an int value to get the array index of the pluralized string
  const pluralIndex = +_getPluralFunction(localeCode)(count);
  let val;
  if (localeCode === 'en-US' && pluralIndex === 1) {
    val = plural;
  } else {
    val = singular;
  }
  // $FlowFixMe i18n._ only accepts 2 arguments
  return i18n._(val, '', pluralIndex);
}

/**
 * We want to restrict the number of ways that dates are displayed.
 *
 * @enum {string}
 */
const DateFormatType = {
  LONG: ('long': 'long'), // e.g. "January 14, 2015"
  YEAR: ('year': 'year'), // e.g. "2015"
  WEEKDAY_DATE: ('weekday_date': 'weekday_date'), // e.g. "Tuesday, January 14, 2015"
  SHORT_WEEKDAY_DATE: ('short_weekday_date': 'short_weekday_date'), //e.g. "Fri, 9/16"
  LONG_MONTH_YEAR: ('long_month_year': 'long_month_year'),
  NUMERIC: ('numeric': 'numeric'), // e.g. "1/14/2015"
  MONTH_DAY: ('month_day': 'month_day'), // e.g. "January 14"
  SHORT_MONTH_DAY: ('short_month_day': 'short_month_day'), // eg. "Jan 14"
};

type DateTimeFormatOptions = {
  localeMatcher?: 'lookup' | 'best fit',
  timeZone?: string,
  hour12?: boolean,
  formatMatcher?: 'basic' | 'best fit',
  weekday?: 'narrow' | 'short' | 'long',
  era?: 'narrow' | 'short' | 'long',
  year?: 'numeric' | '2-digit',
  month?: 'numeric' | '2-digit' | 'narrow' | 'short' | 'long',
  day?: 'numeric' | '2-digit',
  hour?: 'numeric' | '2-digit',
  minute?: 'numeric' | '2-digit',
  second?: 'numeric' | '2-digit',
  timeZoneName?: 'short' | 'long',
};

/**
 * Return the locale string of date
 */
const formatDate = function(
  localeCode: string,
  date: Date,
  dateFormatType: $Values<typeof DateFormatType>,
  useUTC: boolean = true,
): string {
  // Special case where IntlPolyfill seems to do the wrong thing.
  // Formatting a date with options {year: 'numeric'} generates a string with a month -
  // e.g. "1/2015".  There doesn't seem to be a way to get rid of the month.
  // Chrome 41 will generate "2015" for the same inputs, which seems more correct.
  // So, generate year strings outself.

  if (dateFormatType === DateFormatType.YEAR) {
    // $FlowFixMe Date has no method "getYear()"
    return (date.getYear() + 1900).toString();
  } else {
    let options: DateTimeFormatOptions;
    switch (dateFormatType) {
      // The Intl polyfill supports only the minimum "required combinations" listed here:
      // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat
      case DateFormatType.LONG:
        options = {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        };
        break;
      case DateFormatType.NUMERIC:
        options = {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
        };
        break;
      case DateFormatType.WEEKDAY_DATE:
        options = {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        };
        break;
      case DateFormatType.SHORT_WEEKDAY_DATE:
        options = {
          weekday: 'short',
          month: 'numeric',
          day: 'numeric',
        };
        break;
      case DateFormatType.MONTH_DAY:
        options = {
          month: 'long',
          day: 'numeric',
        };
        break;
      case DateFormatType.SHORT_MONTH_DAY:
        options = {
          month: 'short',
          day: 'numeric',
        };
        break;
      case DateFormatType.LONG_MONTH_YEAR:
        options = {
          month: 'long',
          year: 'numeric',
        };
        break;
      default:
        throw new Error('invalid dateFormatType "' + dateFormatType + '"');
    }

    // Ignore the user's timezone and render everything in UTC.  This matches the server
    // date/time formatting.
    if (useUTC) {
      options.timeZone = 'UTC';
    }

    // https://github.com/andyearnshaw/Intl.js/issues/231
    IntlPolyfill.__disableRegExpRestore();

    // TODO: Consider caching the formatter objects
    // We use "IntlPolyfill" instead of depending on the browser-provided version since
    // there are mismatches accross browsers.
    const formatter = new IntlPolyfill.DateTimeFormat(localeCode, options);

    return formatter.format(date);
  }
};

/**
 * Retrieves the decimal separator symbol af a given locale
 */
const getLocaleDecimalSeparator = function(locale: string): string {
  const decimalNumber = 1.1;
  const separator = decimalNumber.toLocaleString(locale).substring(1, 2);
  return separator;
};

/**
 * Localizes a valid decimal number
 * @param {string} number currency value represented as string
 * @param {string} locale locale to which currency value must be formatted
 * @returns {string} localized currency value as string
 */
export const localizeCurrencyValue = function(number: string, locale: string): string {
  return getLocaleDecimalSeparator(locale) === ',' && number && number.length > 0 && !isNaN(number)
    ? number.replace('.', ',')
    : number;
};

/**
 * Unlocalizes a decimal number string to javascript decimal number representation
 * @param {string} number currency value as string
 * @param {string} locale locale from which currency value from be formatted from
 * @returns unlocalized currency value as string
 */
export const unlocalizeCurrencyValue = function(number: string, locale: string): string {
  return getLocaleDecimalSeparator(locale) === ',' && number ? number.replace(',', '.') : number;
};

const locale = {
  DateFormatType,
  formatDate,
  ngettext,
  prettyNumberFormat,
  numberFormat,
  currencySymbols,
  localizeCurrencyValue,
  unlocalizeCurrencyValue,
};

export default locale;
